import type { MailVraagplan, VraagActieBinding } from './mail-vraagplan.js';

/** Server proof, never inferred from a conversation status or model output. */
export interface MailAfdoening {
  versie: 1;
  messageId: string;
  conversationId: string;
  besluitId: string;
  bronHash: string;
  geslotenOp: string;
}
export const AFDOENING_VERB = 'sluit_mail_zonder_antwoord';
export const AFDOENING_CATEGORIEEN = ['commercieel_aanbod', 'afsluitende_bevestiging', 'informatieve_melding'] as const;
/** Same review boundary in the model instruction, verb and employee card.
 * This is not a text classifier; source checks and human approval remain required.
 */
export const AFDOENING_BEOORDELING = 'Lees de hele correspondentie. Er mag geen open klantvraag, toegezegde actie of benodigde opvolging zijn. '
  + 'Deze actie sluit uitsluitend het inboxgesprek. Een verzoek om een werkmelding, order, offerte, factuur of klacht te sluiten, verwerken of bijwerken vraagt de bijbehorende dossierhandeling. '
  + 'Ontbreekt die handeling, houd het verzoek zichtbaar als handmatig werk; gebruik deze sluitactie niet als vervanging. Bevestig geen dossierafhandeling zonder gecontroleerd uitvoeringsresultaat. '
  + 'Een ongevraagd verkoopaanbod zonder bestaande afspraak of verplichting kan ook met een demo- of interessevraag worden afgedaan. '
  + 'Een gevraagd aanbod, leveranciersvraag binnen een bestaande samenwerking of onduidelijke voorgeschiedenis vraagt eerst inhoudelijke beoordeling; stel dan niet zonder antwoord sluiten voor.';

/** Same structural check before publication and approval. This cannot judge
 * whether the model omitted or misunderstood a request in the source text.
 */
export function afdoeningVraagplanBlokkade(plan: MailVraagplan | null, acties: Array<{ verb: string; vraag?: VraagActieBinding }>): string | null {
  const actie = acties[0], vraag = plan?.vragen[0];
  if (!plan || !actie || !vraag || acties.length !== 1 || actie.verb !== AFDOENING_VERB || plan.vragen.length !== 1
    || plan.model_volledig !== true || plan.beperkingen.length || plan.bijlagen.length || !plan.bronnen.length
    || plan.bronnen.some(b => b.gelezen !== b.tekens || b.citaten_afgezonderd)) {
    return 'Afdoening zonder antwoord vereist één sluitvoorstel en een volledig gelezen vraagoverzicht zonder open vragen, andere acties of bronbeperkingen.';
  }
  if (vraag.afhandeling !== 'actie') return 'Deze vraag vraagt nog een antwoord of handmatige verwerking. Sluit het inboxgesprek niet als vervanging.';
  if (vraag.ontbreekt.length) return `Eerst nodig vóór afdoening: ${vraag.ontbreekt.join('; ')}`;
  if (vraag.resterende_acties || vraag.uitgestelde_acties?.length || vraag.afhankelijk_van.length) {
    return 'Er staat nog een benodigde handeling of afhankelijkheid open. Controleer de afhandeling voordat het inboxgesprek wordt gesloten.';
  }
  if (!actie.vraag || actie.vraag.generatie_id !== plan.generatie_id || actie.vraag.vraag_sleutel !== vraag.sleutel
    || vraag.acties.length !== 1 || vraag.acties[0] !== actie.vraag.sleutel || vraag.bron_message_id !== plan.bron_message_id) {
    return 'Het sluitvoorstel is niet eenduidig aan de actuele afdoeningsvraag gekoppeld. Maak een nieuw voorstel.';
  }
  return null;
}
export interface MailAfdoeningHerstel {
  versie: 1; messageId: string; conversationId: string; besluitId: string; heropendOp: string; door: string;
}
const uuid = (v: unknown): v is string => typeof v === 'string' && /^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(v);
const timestamp = (v: unknown): v is string => typeof v === 'string' && Number.isFinite(Date.parse(v));
export function leesMailAfdoeningHerstel(raw: unknown): MailAfdoeningHerstel | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (r.versie !== 1 || !uuid(r.messageId) || !uuid(r.conversationId) || !uuid(r.besluitId)
    || !timestamp(r.heropendOp) || typeof r.door !== 'string' || !r.door.trim()) return null;
  return { versie: 1, messageId: r.messageId, conversationId: r.conversationId, besluitId: r.besluitId,
    heropendOp: r.heropendOp, door: r.door };
}
export function leesMailAfdoening(raw: unknown): MailAfdoening | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const b = raw as Record<string, unknown>;
  if (b.versie !== 1 || !uuid(b.messageId) || !uuid(b.conversationId) || !uuid(b.besluitId)
    || typeof b.bronHash !== 'string' || !/^[a-f0-9]{64}$/.test(b.bronHash)
    || !timestamp(b.geslotenOp)) return null;
  return { versie: 1, messageId: b.messageId as string, conversationId: b.conversationId as string,
    besluitId: b.besluitId as string, bronHash: b.bronHash, geslotenOp: b.geslotenOp };
}
